import api from '../utils/api';

export const EMAIL_TYPES = {
  APPLICATION_SUBMITTED: 'Application Submitted',
  APPLICATION_INVITATION: 'Application Invitation',
  INTERVIEW_SCHEDULED: 'Interview Scheduled',
  INTERVIEW_ACCEPTED: 'Interview Accepted',
  INTERVIEW_REJECTED: 'Interview Rejected',
  ONBOARDING_COMPLETED: 'Onboarding Completed',
  ACCOUNT_ARCHIVED: 'Account Archived',
  ACCOUNT_REACTIVATED: 'Account Reactivated',
  APPLICATION_ACCEPTED: 'Application Accepted',
  APPLICATION_REJECTED: 'Application Rejected',
  APPLICATION_REISSUED: 'Application Reissued',
  WELCOME_EMAIL: 'Welcome Email',
  PASSWORD_RESET: 'Password Reset',
  PASSWORD_RESET_CONFIRMATION: 'Password Reset Confirmation'
} as const;

export type EmailType = keyof typeof EMAIL_TYPES;

export interface EmailTemplate {
  _id?: string;
  name: string;
  type: EmailType;
  subject: string;
  body: string;
  active: boolean;
  audience?: string[];
  createdAt?: string;
  updatedAt?: string;
}

// ADMIN-02: the server never returns the real brevoApiKey - only whether
// one is configured. `brevoApiKey` below is write-only: set it to submit a
// new/replacement key, omit it to leave the existing one unchanged.
export interface EmailConfigUpdate {
  brevoApiKey?: string;
  senderName?: string;
  senderEmail?: string;
}

export interface EmailConfigResponse {
  _id?: string;
  brevoApiKeyConfigured: boolean;
  senderName: string;
  senderEmail: string;
  createdAt?: string;
  updatedAt?: string;
  templates?: EmailTemplate[];
}

const BASE_URL = '/api/email-config';

// Fetch email configuration
export const fetchEmailConfig = async (): Promise<EmailConfigResponse> => {
  try {
    const [configResponse, templatesResponse] = await Promise.all([
      api.get(`${BASE_URL}/config`),
      api.get(`${BASE_URL}/templates`)
    ]);

    if (!configResponse.data || typeof configResponse.data.senderEmail !== 'string') {
      console.error('Invalid email config data:', configResponse.data);
      throw new Error('Invalid email configuration data received');
    }

    return {
      ...configResponse.data,
      templates: templatesResponse.data || []
    };
  } catch (error) {
    console.error('Error fetching email config:', error);
    throw error;
  }
};

// Update email configuration
export const updateEmailConfig = async (config: EmailConfigUpdate) => {
  try {
    const response = await api.put(`${BASE_URL}/config`, config);
    return response.data;
  } catch (error) {
    console.error('Error updating email config:', error);
    throw error;
  }
};

// Send test email
export const sendTestEmail = async (email: string) => {
  try {
    const response = await api.post(`${BASE_URL}/test-email`, { email });
    return response.data;
  } catch (error) {
    console.error('Error sending test email:', error);
    throw error;
  }
};